import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import { useAuthStore } from '@/stores/auth.store'
import { useControlCenterStore } from '@/stores/controlCenter.store'
import type { FeatureFlag } from '@/types/controlCenter'
import { cn } from '@/utils/cn'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import EditFlagModal from '../components/EditFlagModal'
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal'
import RowActions from '../components/shared/RowActions'
import SortIcon, { type SortDir } from '../components/shared/SortIcon'
import TablePagination from '../components/shared/TablePagination'
import { thCls, thSortBtnCls, tdCls } from '../components/shared/tableStyles'
import { fmtDateTime } from '../utils/format'
import { CLIENT_BADGE, COLUMN_CONFIG, PAGE_SIZE } from './constants'
import useFeatureFlagsData from './hooks/useFeatureFlagsData'
import { QUERY_KEYS } from '../utils/queryKeys'
import type { SortField } from './types'

function sortValue(flag: FeatureFlag, field: SortField): string | number {
  if (field === 'global') return flag.global ? 1 : 0
  return flag[field] ?? ''
}

interface FeatureFlagsProps {
  clientNameToId?: Record<string, number>
  isCreating?: boolean
  onCreateClose?: () => void
}

const FeatureFlags = ({
  clientNameToId,
  isCreating = false,
  onCreateClose,
}: FeatureFlagsProps) => {
  const queryClient = useQueryClient()
  const { flagsSearchQuery, flagStatusFilter } = useControlCenterStore()
  const role = useAuthStore((s) => s.permissions?.role)

  const { flags } = useFeatureFlagsData()

  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(0)
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
  const [deletingFlag, setDeletingFlag] = useState<FeatureFlag | null>(null)

  useEffect(() => {
    setPage(0)
  }, [flagsSearchQuery, flagStatusFilter])

  const deleteMutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.featureFlags,
      })
      setDeletingFlag(null)
    },
  })

  const toggleSort = (field: SortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
  }

  const q = flagsSearchQuery.toLowerCase()
  const filtered = flags
    .filter((f) => {
      return (
        (flagStatusFilter === 'all' || f.status === flagStatusFilter) &&
        (q === '' ||
          f.name.toLowerCase().includes(q) ||
          (f.description ?? '').toLowerCase().includes(q))
      )
    })
    .sort((a, b) => {
      const va = sortValue(a, sortField)
      const vb = sortValue(b, sortField)
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const canEdit = role === 'SUPER_ADMIN'

  return (
    <div className='flex flex-1 overflow-hidden'>
      <div className='border-border-main bg-neutral-dimmed-heavy flex flex-1 flex-col overflow-hidden rounded-lg border'>
        {/* Table */}
        <div className='flex-1 overflow-auto'>
          <Table className='table-auto'>
            <TableHeader className='border-border-main bg-neutral-dimmed sticky top-0 border-b'>
              <TableRow className='border-border-main hover:bg-transparent'>
                {COLUMN_CONFIG.map((col) => {
                  const isSortable = col.sortable && col.key !== 'actions'
                  const isActive = sortField === col.key
                  return (
                    <TableHead
                      key={col.key}
                      className={cn(thCls, col.className)}
                      aria-sort={
                        isSortable
                          ? isActive
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                    >
                      {isSortable ? (
                        <button
                          type='button'
                          className={thSortBtnCls}
                          onClick={() => toggleSort(col.key as SortField)}
                        >
                          {col.label}
                          <SortIcon
                            field={col.key}
                            sortField={sortField}
                            sortDir={sortDir}
                          />
                        </button>
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody className='divide-border-main divide-y'>
              {paged.map((flag) => {
                const status = flag.status
                const isEnabled = status === 'enabled'
                return (
                  <TableRow
                    key={flag.id}
                    className='border-border-main hover:bg-neutral-dimmed transition'
                  >
                    <TableCell
                      className={cn(
                        tdCls,
                        'w-[40%] max-w-[40%] min-w-[40%] overflow-hidden',
                      )}
                    >
                      <span className='text-text-primary block font-semibold break-all'>
                        {flag.name}
                      </span>
                      {flag.description && (
                        <span className='text-text-tertiary mt-0.5 block text-xs break-all'>
                          {flag.description}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={tdCls}>
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize',
                          flag.client
                            ? CLIENT_BADGE[flag.client]
                            : 'border-border-main bg-neutral-dimmed text-text-tertiary border',
                        )}
                      >
                        {flag.client ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell className={tdCls}>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize',
                          isEnabled &&
                            'border border-emerald-500/40 bg-emerald-500/20 text-emerald-300',
                          !isEnabled &&
                            'border-border-main bg-neutral-dimmed text-text-tertiary border',
                        )}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            isEnabled && 'bg-emerald-400',
                            !isEnabled && 'bg-text-dimmed',
                          )}
                        />
                        {isEnabled ? 'enabled' : 'disabled'}
                      </span>
                    </TableCell>
                    <TableCell className={tdCls}>
                      <span className='text-text-tertiary'>
                        {flag.global ? 'Yes' : 'No'}
                      </span>
                    </TableCell>
                    <TableCell className={cn(tdCls, 'whitespace-nowrap')}>
                      <span className='text-text-tertiary'>
                        {fmtDateTime(flag.lastModified)}
                      </span>
                    </TableCell>
                    <TableCell className={tdCls}>
                      <RowActions
                        itemName={flag.name}
                        onEdit={
                          canEdit ? () => setEditingFlag(flag) : undefined
                        }
                        onDelete={
                          canEdit ? () => setDeletingFlag(flag) : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
              {paged.length === 0 && (
                <TableRow className='hover:bg-transparent'>
                  <TableCell
                    colSpan={COLUMN_CONFIG.length}
                    className='text-text-tertiary py-12 text-center text-sm'
                  >
                    No flags match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        )}
      </div>

      {(editingFlag || isCreating) && (
        <EditFlagModal
          flag={editingFlag ?? undefined}
          onClose={
            editingFlag ? () => setEditingFlag(null) : () => onCreateClose?.()
          }
          clientNameToId={clientNameToId}
        />
      )}

      {deletingFlag && (
        <ConfirmDeleteModal
          title='Delete Feature Flag'
          itemName={deletingFlag.name}
          isPending={deleteMutation.isPending}
          error={deleteMutation.error}
          onConfirm={() =>
            deleteMutation.mutate({
              method: 'delete',
              url: `${CONTROL_CENTRE_API_DOMAIN}/admin/feature_flags/${deletingFlag.id}`,
              data: {},
            })
          }
          onCancel={() => setDeletingFlag(null)}
        />
      )}
    </div>
  )
}

export default FeatureFlags
